import { useRef, useEffect, useState } from "react";

type UseLazyImageOptions = {
  /** Shown before the real image loads. Defaults to empty string (blank). */
  placeholder?: string;
  /**
   * Scroll container to use as the IntersectionObserver root.
   * Pass null to fall back to the document viewport.
   */
  containerRef?: React.RefObject<Element>;
  /** Pre-load margin around the root. Defaults to "100px". */
  rootMargin?: string;
};

type UseLazyImageReturn = {
  /** Attach this ref to the <img> element. */
  imgRef: React.RefObject<HTMLImageElement>;
  /** The src to put on the <img> element. */
  src: string;
};

/**
 * Defers loading an image until it enters the visible area of a scroll container
 * (or the viewport when containerRef is omitted).
 *
 * Usage:
 *   const { imgRef, src } = useLazyImage(realUrl, { containerRef, placeholder: NotFoundImg });
 *   <img ref={imgRef} src={src} />
 */
export function useLazyImage(realSrc: string | null, options: UseLazyImageOptions = {}): UseLazyImageReturn {
  const { placeholder = "", containerRef, rootMargin = "100px" } = options;
  const imgRef = useRef<HTMLImageElement>(null);
  const [src, setSrc] = useState<string>(realSrc === null ? placeholder || "" : placeholder);

  useEffect(() => {
    // No real image to load — placeholder is already the final src.
    if (realSrc === null) return;

    const el = imgRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSrc(realSrc);
          observer.disconnect();
        }
      },
      {
        root: containerRef?.current ?? null,
        rootMargin,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [realSrc, containerRef, rootMargin]);

  return { imgRef, src };
}
